import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Plus, ShieldAlert, Bell, Activity, Users, Clock,
  FileText, AlertTriangle, CheckCircle2, ChevronRight,
  Hospital, Eye, Stethoscope, Info, CalendarDays,
} from 'lucide-react';
import {
  mmgKPI, dimissioniProtette, notificheMMG,
} from '@/data/mockMMGData';
import { pazientiInCaricoMMG } from '@/data/mockMMGData';

const prioritaStyle = {
  critica: { dot: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-600' },
  alta: { dot: 'bg-orange-500', bg: 'bg-orange-50', border: 'border-orange-100', text: 'text-orange-600' },
  media: { dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-600' },
  bassa: { dot: 'bg-slate-400', bg: 'bg-slate-50', border: 'border-slate-100', text: 'text-slate-500' },
};

const tipoIcon = {
  validazione: { icon: FileText, color: '#8B5CF6' },
  'alert-clinico': { icon: Activity, color: '#EF4444' },
  amministrativo: { icon: CheckCircle2, color: '#10B981' },
};

/* ═══════════ WIDGET A: DIMISSIONI PROTETTE ═══════════ */
function DimissioniProtette({ dimissioni }) {
  if (!dimissioni.length) return null;
  const d = dimissioni[0];
  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl border-l-4 border-l-red-400 overflow-hidden" data-testid="widget-dimissioni">
      <CardContent className="p-0">
        <div className="relative px-6 pt-5 pb-4">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/4 via-orange-500/3 to-transparent" />
          <div className="relative flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Hospital className="w-5 h-5 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-bold text-red-700">Dimissione Protetta</h3>
                <Badge className="bg-red-100 text-red-600 border-0 text-[9px] font-bold animate-pulse">URGENTE</Badge>
              </div>
              <p className="text-xs font-semibold text-foreground">
                {d.pazienteNome}, {d.pazienteEta} anni — in dimissione da {d.reparto}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">{d.diagnosi}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 italic">{d.note}</p>
              <p className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-1">
                <Clock className="w-3 h-3" />Data prevista dimissione: {d.dataDimissione}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 px-6 py-3 border-t border-red-100 bg-red-50/30">
          <Button variant="outline" size="sm" className="gap-1.5 rounded-xl text-xs h-8 border-red-200 text-red-600 hover:bg-red-50" data-testid="btn-lettera-dimissione">
            <Eye className="w-3.5 h-3.5" />Vedi Lettera Dimissione
          </Button>
          <Button size="sm" className="gap-1.5 rounded-xl text-xs h-8 bg-red-600 hover:bg-red-700 text-white" data-testid="btn-visiona-pai">
            <FileText className="w-3.5 h-3.5" />Visiona PAI
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════ WIDGET B: CENTRO NOTIFICHE ═══════════ */
function CentroNotifiche({ notifiche, onNotificaClick }) {
  const [filter, setFilter] = useState('tutti');
  const informative = notifiche.filter(n => n.tipo !== 'alert-clinico');
  const filtered = filter === 'tutti' ? informative : informative.filter(n => n.tipo === filter);

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="widget-notifiche">
      <CardContent className="p-0">
        <div className="px-6 py-4 border-b border-border/30">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center">
                <Bell className="w-4 h-4 text-indigo-500" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Centro Notifiche</h2>
                <p className="text-[10px] text-muted-foreground">{notifiche.filter(n => !n.letto).length} non letti</p>
              </div>
            </div>
          </div>
          <div className="flex gap-1.5">
            {[
              { key: 'tutti', label: 'Tutti' },
              { key: 'validazione', label: 'Transizioni' },
              { key: 'amministrativo', label: 'Amministrativi' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${filter === f.key ? 'bg-primary/10 text-primary' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-border/20" data-testid="notifiche-list">
          <AnimatePresence mode="popLayout">
            {filtered.map((n) => {
              const pStyle = prioritaStyle[n.priorita] || prioritaStyle.bassa;
              const tIcon = tipoIcon[n.tipo] || tipoIcon.amministrativo;
              const Icon = tIcon.icon;
              return (
                <motion.div
                  key={n.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={`px-6 py-3.5 flex items-start gap-3 hover:bg-muted/20 transition-colors cursor-pointer ${!n.letto ? 'bg-primary/[0.02]' : ''}`}
                  onClick={() => onNotificaClick(n)}
                  data-testid={`notifica-${n.id}`}
                >
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${tIcon.color}10` }}>
                    <Icon className="w-4 h-4" style={{ color: tIcon.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {!n.letto && <div className={`w-1.5 h-1.5 rounded-full ${pStyle.dot}`} />}
                      <p className={`text-xs font-semibold ${!n.letto ? 'text-foreground' : 'text-muted-foreground'}`}>{n.titolo}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.descrizione}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <Badge className={`text-[8px] border-0 ${pStyle.bg} ${pStyle.text}`}>{n.priorita}</Badge>
                      <span className="text-[9px] text-muted-foreground">{n.timestamp}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0 mt-2" />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════ PAGE PRINCIPALE ═══════════ */
export default function MMGDashboard() {
  const navigate = useNavigate();

  return (
    <AppLayout role="mmg">
      <div className="space-y-6" data-testid="mmg-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground" data-testid="mmg-title">Dashboard Operativa</h1>
            <p className="text-sm text-muted-foreground mt-1">Centro di controllo — Assistenza Domiciliare Integrata</p>
          </div>
          <Button
            className="gap-2 rounded-xl h-11 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm"
            onClick={() => navigate('/mmg/nuova-richiesta')}
            data-testid="cta-nuova-richiesta"
          >
            <Plus className="w-4 h-4" />Crea Nuova Richiesta ADI
          </Button>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mmg-kpi-strip">
          {[
            { label: 'Pazienti attivi in ADI', value: 14, icon: Users, color: '#3B82F6', bg: 'bg-blue-50', path: '/mmg/pazienti' },
            { label: 'Richieste inviate alla COT', value: 2, icon: Clock, color: '#F59E0B', bg: 'bg-amber-50', path: '/mmg/richieste' },
            { label: 'In scadenza (prossimi 30gg)', value: 3, icon: CalendarDays, color: '#6366F1', bg: 'bg-indigo-50', path: '/mmg/pazienti' },
            { label: 'Da effettuare (Programmato MMG)', value: 5, icon: Stethoscope, color: '#10B981', bg: 'bg-emerald-50', path: '/mmg/programmato' },
          ].map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card
                key={kpi.label}
                className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl cursor-pointer hover:shadow-md hover:bg-muted/10 transition-all duration-200 group"
                onClick={() => navigate(kpi.path)}
                data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5" style={{ color: kpi.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                    <p className="text-[11px] text-muted-foreground font-medium">{kpi.label}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors flex-shrink-0" />
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Widget B: Centro Notifiche */}
        <CentroNotifiche
          notifiche={notificheMMG}
          onNotificaClick={(n) => {
            const match = pazientiInCaricoMMG.find(p => p.nome === n.paziente);
            if (match) {
              navigate(`/mmg/pazienti/${match.id}`);
            } else {
              navigate('/mmg/pazienti');
            }
          }}
        />
      </div>
    </AppLayout>
  );
}
